import React, { useState, useEffect, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { useTenant } from "../../context/TenantContext";
import umService from "../../services/university-admin/umService";
import { formatNumericDate } from "../../utils/formatters";

const RECENT_ACTIVITY_SIZE = 8;

const STATUS_LABEL_KEYS = {
  COMPLETED: "completed",
  PENDING: "pending",
  REFUND_REQUESTED: "refundReq",
  REFUNDED: "refunded",
  REJECTED: "rejected",
  CANCELLED: "cancelled",
};

const UMTransactionRecentActivity = () => {
  const { t, i18n } = useTranslation("universityManager");
  const { userRole, currentTenantId } = useTenant();

  const [recentActivity, setRecentActivity] = useState([]);
  const [loading, setLoading] = useState(true);

  const loadRecentActivity = useCallback(async () => {
    setLoading(true);
    const res = await umService.getTransactions({ userRole, currentTenantId, page: 0, size: RECENT_ACTIVITY_SIZE });
    setRecentActivity(res.success ? res.data.content : []);
    setLoading(false);
  }, [userRole, currentTenantId]);

  useEffect(() => {
    loadRecentActivity();
  }, [loadRecentActivity]);

  const getActivityMessage = (txn) => {
    const name = txn.userFullName;
    if (txn.status === "COMPLETED") return t(`finance.dashboard.messages.paid${txn.transactionType === "EVENT_TICKET" ? "Event" : "Club"}`, { name });
    if (txn.status === "PENDING") return t(`finance.dashboard.messages.pending${txn.transactionType === "EVENT_TICKET" ? "Event" : "Club"}`, { name });
    if (txn.status === "REFUND_REQUESTED") return t(`finance.dashboard.messages.refundReq${txn.transactionType === "EVENT_TICKET" ? "Event" : "Club"}`, { name });
    if (txn.status === "REFUNDED") return t(`finance.dashboard.messages.refunded${txn.transactionType === "EVENT_TICKET" ? "Event" : "Club"}`, { name });
    if (txn.status === "REJECTED") return t("finance.dashboard.messages.rejected", { name });
    if (txn.status === "CANCELLED") return t("finance.dashboard.messages.cancelled", { name });
    return t("finance.dashboard.messages.updated", { name });
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case "COMPLETED": return <div className="bg-success text-white rounded-circle d-flex align-items-center justify-content-center" style={{ width: 32, height: 32 }}><i className="fas fa-check"></i></div>;
      case "PENDING": return <div className="bg-warning text-white rounded-circle d-flex align-items-center justify-content-center" style={{ width: 32, height: 32 }}><i className="fas fa-hourglass-half"></i></div>;
      case "REFUND_REQUESTED": return <div className="bg-info text-white rounded-circle d-flex align-items-center justify-content-center" style={{ width: 32, height: 32 }}><i className="fas fa-undo"></i></div>;
      case "REFUNDED": return <div className="bg-secondary text-white rounded-circle d-flex align-items-center justify-content-center" style={{ width: 32, height: 32 }}><i className="fas fa-reply"></i></div>;
      default: return <div className="bg-secondary text-white rounded-circle d-flex align-items-center justify-content-center" style={{ width: 32, height: 32 }}><i className="fas fa-circle"></i></div>;
    }
  };

  if (!loading && recentActivity.length === 0) {
    return null;
  }

  return (
    <div className="card border-0 shadow-sm mb-4">
      <div className="card-header bg-white border-bottom py-3">
        <h6 className="mb-0 fw-bold">{t("finance.dashboard.recentActivity")}</h6>
      </div>
      <div className="card-body p-0">
        {loading ? (
          <div className="text-center py-5">
            <div className="spinner-border text-primary" role="status" />
          </div>
        ) : recentActivity.length === 0 ? (
          <div className="text-center py-5 text-muted">{t("finance.dashboard.noRecentActivity")}</div>
        ) : (
          <ul className="list-group list-group-flush">
            {recentActivity.map((txn) => (
              <li key={txn.transactionId} className="list-group-item px-4 py-3 d-flex align-items-center">
                <div className="me-3">{getStatusIcon(txn.status)}</div>
                <div className="flex-grow-1">
                  <h6 className="mb-0 fw-semibold">{getActivityMessage(txn)}</h6>
                  <small className="text-muted">
                    {formatNumericDate(txn.createdAt, i18n.language)} • {txn.referenceCode} • {txn.transactionId}
                  </small>
                </div>
                <div className="text-end">
                  <div className="fw-bold">{Number(txn.amount).toLocaleString(i18n.language, { minimumFractionDigits: 2 })} {txn.currency}</div>
                  <span className="badge bg-light text-dark border">
                    {STATUS_LABEL_KEYS[txn.status] ? t(`finance.ledger.${STATUS_LABEL_KEYS[txn.status]}`) : txn.status}
                  </span>
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
};

export default UMTransactionRecentActivity;
